export const PROJECT_ACCESS_ROLES = ['owner', 'ad_editor', 'viewer'] as const;

export type ProjectAccessRole = typeof PROJECT_ACCESS_ROLES[number];
export type CollaboratorRole = Exclude<ProjectAccessRole, 'owner'>;

export const COLLABORATOR_ROLES: readonly CollaboratorRole[] = [
    'ad_editor',
    'viewer',
];

export const PROJECT_ROLE_LABELS: Readonly<Record<ProjectAccessRole, string>> = {
    owner: 'Owner',
    ad_editor: 'AD / Editor',
    viewer: 'Viewer',
};

export interface DatabaseCapabilities {
    projectDashboardVersion?: number;
    calendarEditingVersion?: number;
    projectAccessVersion: number;
    stage: 'legacy' | 'expand' | 'contract';
    sharedReads: boolean;
    directProjectDml: boolean;
    planningAccessVersion: number;
    planningStage: 'legacy' | 'expand' | 'contract';
    directPlanningDml: boolean;
    planningCommands: boolean;
    documentSettingsVersion: number;
    directDocumentSettingsDml: boolean;
    documentSettingsCommands: boolean;
    collaborationVersion: number;
    privateProjectChannels: boolean;
    activityBroadcasts: boolean;
}

export const LEGACY_DATABASE_CAPABILITIES: DatabaseCapabilities = Object.freeze({
    projectAccessVersion: 0,
    stage: 'legacy',
    sharedReads: false,
    directProjectDml: true,
    planningAccessVersion: 0,
    planningStage: 'legacy',
    directPlanningDml: true,
    planningCommands: false,
    documentSettingsVersion: 0,
    directDocumentSettingsDml: true,
    documentSettingsCommands: false,
    collaborationVersion: 0,
    privateProjectChannels: false,
    activityBroadcasts: false,
});

export interface ProjectCapabilities {
    canRead: boolean;
    canExport: boolean;
    canManageAccess: boolean;
    canEditProject: boolean;
    canEditBreakdown: boolean;
    canEditPlanning: boolean;
    canManageLocations: boolean;
    canManageVersions: boolean;
    canEditCallSheets: boolean;
    canManageDocumentSettings: boolean;
}

export interface ProjectAccessSnapshot {
    projectId: string;
    role: ProjectAccessRole;
    database: DatabaseCapabilities;
    capabilities: ProjectCapabilities;
    deletionRequestedAt: string | null;
    purgeAfter: string | null;
}

export interface ProjectMemberSummary {
    userId: string;
    role: ProjectAccessRole;
    displayName: string;
    avatarUrl: string | null;
    joinedAt: string | null;
    isCurrentUser?: boolean;
}

export interface ProjectInvitationSummary {
    id: string;
    projectId: string;
    email: string;
    role: CollaboratorRole;
    createdAt: string;
    expiresAt: string;
    revokedAt: string | null;
    acceptedAt: string | null;
}

export interface CreatedProjectInvitation {
    invitation: ProjectInvitationSummary;
    inviteUrl: string;
}

export interface ProjectActivitySummary {
    id: number;
    action: string;
    actorId: string;
    actorName: string;
    details: Readonly<Record<string, unknown>>;
    createdAt: string;
}

export const isProjectAccessRole = (value: unknown): value is ProjectAccessRole => (
    typeof value === 'string'
    && (PROJECT_ACCESS_ROLES as readonly string[]).includes(value)
);

export const isCollaboratorRole = (value: unknown): value is CollaboratorRole => (
    value === 'ad_editor' || value === 'viewer'
);

export const normalizeProjectRole = (value: unknown): ProjectAccessRole | null => (
    isProjectAccessRole(value) ? value : null
);

export const deriveProjectCapabilities = (
    role: ProjectAccessRole,
    database: DatabaseCapabilities,
    pendingDeletion = false,
): ProjectCapabilities => {
    const isOwner = role === 'owner';
    const sharedRoleCanRead = database.sharedReads && role !== 'owner';
    const canRead = isOwner || sharedRoleCanRead;
    const ownerCanMutate = isOwner && !pendingDeletion;
    const protectedPlanningReady = database.planningAccessVersion >= 1;
    const ownerCanUseProtectedWriters = ownerCanMutate && protectedPlanningReady;

    // Fail closed while a database is still on Phase 2/Phase 3 expand v0. The
    // browser no longer contains direct protected-table fallbacks. AD planning
    // additionally requires the final contract flag after stale clients drain.
    const adCanPlan = protectedPlanningReady
        && role === 'ad_editor'
        && database.planningCommands
        && !pendingDeletion;
    const ownerCanManageDocumentSettings = ownerCanMutate
        && database.documentSettingsVersion >= 1
        && database.documentSettingsCommands
        && !database.directDocumentSettingsDml;

    return Object.freeze({
        canRead,
        canExport: canRead,
        canManageAccess: ownerCanMutate,
        canEditProject: ownerCanMutate,
        canEditBreakdown: ownerCanUseProtectedWriters,
        canEditPlanning: ownerCanUseProtectedWriters || adCanPlan,
        canManageLocations: ownerCanUseProtectedWriters,
        canManageVersions: ownerCanUseProtectedWriters,
        canEditCallSheets: ownerCanUseProtectedWriters || adCanPlan,
        canManageDocumentSettings: ownerCanManageDocumentSettings,
    });
};

export const createProjectAccessSnapshot = (
    projectId: string,
    role: ProjectAccessRole,
    database: DatabaseCapabilities,
    recovery: { deletionRequestedAt?: string | null; purgeAfter?: string | null } = {},
): ProjectAccessSnapshot => {
    const deletionRequestedAt = recovery.deletionRequestedAt || null;
    return {
        projectId,
        role,
        database,
        capabilities: deriveProjectCapabilities(role, database, Boolean(deletionRequestedAt)),
        deletionRequestedAt,
        purgeAfter: recovery.purgeAfter || null,
    };
};
